import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useSiteSeo } from '@/lib/useSiteSeo';
import { fmtDate } from '@/lib/utils';

const CATEGORIES = [
  { key: 'all', label: 'ALL' },
  { key: 'guides', label: 'BUILD GUIDES' },
  { key: 'tips', label: 'SETUP TIPS' },
  { key: 'news', label: 'NEWS' },
  { key: 'reviews', label: 'REVIEWS' },
];

export default function Blog() {
  const [category, setCategory] = useState('all');

  useSiteSeo('blog', {
    title: 'Sim Racing Guides & Build Tips',
    description: 'Rig build guides, direct drive explainers, sim setup tips and product reviews from the Motion Reality team.',
  });

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: () => base44.entities.BlogPost.filter({ published: true }, '-published_date', 100),
  });

  const filtered = useMemo(() => {
    let list = posts;
    if (category !== 'all') list = list.filter((p) => p.category === category);
    return list;
  }, [posts, category]);

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-20 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">GUIDES &amp; INSIGHTS</p>
            <h1 className="text-white font-black text-4xl md:text-6xl uppercase leading-tight mb-3">THE BLOG</h1>
            <p className="text-white/40 text-sm max-w-lg">Rig build guides, direct drive explainers, sim setup tips and reviews — written by sim racers, for sim racers.</p>
          </motion.div>
        </div>
      </section>

      <div className="border-b border-white/5 bg-[#0d0d0f] sticky top-[var(--header-offset)] z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center overflow-x-auto gap-0 scrollbar-hide">
          {CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => setCategory(c.key)}
              className={`shrink-0 text-xs font-bold tracking-wider px-5 py-4 border-b-2 transition-all whitespace-nowrap ${category === c.key ? 'border-primary text-primary' : 'border-transparent text-white/30 hover:text-white/60'}`}>
              {c.label}
            </button>
          ))}
          <span className="text-white/20 text-xs font-mono ml-auto shrink-0 pl-4">{filtered.length} POST{filtered.length !== 1 ? 'S' : ''}</span>
        </div>
      </div>

      <section className="px-6 lg:px-10 py-12 max-w-7xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-40"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-40"><p className="text-white/30 text-sm">No posts yet — check back soon.</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post, i) => (
              <motion.div key={post.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={`/blog/${post.slug}`} className="group block bg-[#111115] hover:bg-[#1a1a1f] transition-colors h-full">
                  <div className="aspect-[16/10] bg-[#0d0d0f] overflow-hidden">
                    {post.cover_image ? (
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/10 text-xs">NO IMAGE</div>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="text-primary text-[10px] font-bold tracking-widest mb-2">{post.category?.toUpperCase()}</p>
                    <h3 className="text-white font-bold text-lg leading-snug mb-3 line-clamp-2 group-hover:text-primary transition-colors">{post.title}</h3>
                    <p className="text-white/40 text-sm leading-relaxed line-clamp-3 mb-4">{post.excerpt || ''}</p>
                    <div className="flex items-center justify-between text-white/30 text-[10px] font-bold tracking-widest">
                      <span>{post.author_name || 'MOTION REALITY'}</span>
                      {post.published_date && <span>{fmtDate(post.published_date)}</span>}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}